/**
 * 어드민 Alcohol API 서비스
 */

import { normalizeError } from '@/lib/api-error';
import { api } from '@/lib/axios';
import { apiClient } from '@/lib/api-client';
import { createQueryKeys } from '@/hooks/useApiQuery';
import {
  AlcoholApi,
  type AlcoholSearchParams,
  type AlcoholListItem,
  type AlcoholPageMeta,
  type AlcoholLookupParams,
  type AlcoholLookupItem,
  type AlcoholDetail,
  type AlcoholCreateRequest,
  type AlcoholCreateResponse,
  type AlcoholDeleteResponse,
  type AlcoholUpdateRequest,
  type AlcoholUpdateResponse,
  type CategoryReferenceMap,
  type AlcoholExcelValidationResult,
  type AlcoholBulkRequest,
  type AlcoholBulkValidationResult,
  type AlcoholBulkCreateResult,
} from '@/types/api';

// ============================================
// Query Keys
// ============================================

export const adminAlcoholKeys = createQueryKeys('admin-alcohols');

// ============================================
// 응답 타입 (페이지네이션 포함)
// ============================================

export interface AlcoholListResponse {
  items: AlcoholListItem[];
  meta: AlcoholPageMeta;
}

export interface AlcoholLookupResponse {
  items: AlcoholLookupItem[];
  meta: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
  };
}

// ============================================
// Service
// ============================================

export const adminAlcoholService = {
  /**
   * 술 목록 조회
   * 페이지 기반 페이지네이션 (meta에 페이지 정보 포함)
   */
  search: async (params?: AlcoholSearchParams): Promise<AlcoholListResponse> => {
    const response = await apiClient.get<AlcoholListItem[]>(AlcoholApi.search.endpoint, {
      params,
    });

    // data는 배열, meta에 페이지 정보 직접 포함
    return {
      items: response.data ?? [],
      meta: {
        page: response.meta.page ?? params?.page ?? 0,
        size: response.meta.size ?? params?.size ?? 20,
        totalElements: response.meta.totalElements ?? 0,
        totalPages: response.meta.totalPages ?? 0,
        hasNext: response.meta.hasNext ?? false,
      },
    };
  },

  /**
   * 술 lookup 조회
   * 선택 컴포넌트용 핵심 필드 + 페이지 기반 페이지네이션
   */
  lookup: async (params?: AlcoholLookupParams): Promise<AlcoholLookupResponse> => {
    const response = await apiClient.get<AlcoholLookupItem[]>(AlcoholApi.lookup.endpoint, {
      params,
    });
    const page = response.meta.page ?? params?.page ?? 0;
    const size = response.meta.size ?? params?.size ?? 20;
    const totalPages = response.meta.totalPages ?? 0;

    return {
      items: response.data ?? [],
      meta: {
        page,
        size,
        totalElements: response.meta.totalElements ?? 0,
        totalPages,
        hasNext: response.meta.hasNext ?? page + 1 < totalPages,
      },
    };
  },

  /**
   * 술 상세 조회
   */
  getDetail: async (alcoholId: number): Promise<AlcoholDetail> => {
    const endpoint = AlcoholApi.detail.endpoint.replace(':alcoholId', String(alcoholId));
    const response = await apiClient.get<AlcoholDetail>(endpoint);
    return response.data;
  },

  /**
   * 술 생성
   */
  create: async (data: AlcoholCreateRequest): Promise<AlcoholCreateResponse> => {
    return apiClient.post<AlcoholCreateResponse, AlcoholCreateRequest>(
      AlcoholApi.create.endpoint,
      data
    );
  },

  /**
   * 카테고리 레퍼런스 조회
   * 서버 응답은 그룹(AlcoholCategory)별로 묶인 카테고리 페어 목록.
   */
  getCategoryReferences: async (): Promise<CategoryReferenceMap> => {
    const response = await apiClient.get<CategoryReferenceMap>(
      AlcoholApi.categoryReference.endpoint
    );
    return response.data;
  },

  downloadExcelTemplate: async (): Promise<ArrayBuffer> => {
    try {
      const response = await api.get<ArrayBuffer>(AlcoholApi.excelTemplate.endpoint, {
        responseType: 'arraybuffer',
      });
      return response.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  validateExcel: (file: File): Promise<AlcoholExcelValidationResult> => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post<AlcoholExcelValidationResult, FormData>(
      AlcoholApi.excelValidate.endpoint,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  /**
   * 알코올 JSON 목록 검증
   * 저장하지 않고 행별 오류·경고와 정규화 결과를 반환한다.
   */
  validateBulk: (data: AlcoholBulkRequest): Promise<AlcoholBulkValidationResult> => {
    return apiClient.post<AlcoholBulkValidationResult, AlcoholBulkRequest>(
      AlcoholApi.bulkValidate.endpoint,
      data
    );
  },

  /**
   * 알코올 목록 일괄 등록
   * 서버에서 전체 행을 다시 검증하며, 한 행이라도 실패하면 전혀 저장하지 않는다.
   */
  createBulk: (data: AlcoholBulkRequest): Promise<AlcoholBulkCreateResult> => {
    return apiClient.post<AlcoholBulkCreateResult, AlcoholBulkRequest>(
      AlcoholApi.bulkCreate.endpoint,
      data
    );
  },

  /**
   * 술 삭제
   * 소프트 삭제로 처리되며, 리뷰/평점이 있는 술은 삭제 불가
   */
  delete: async (alcoholId: number): Promise<AlcoholDeleteResponse> => {
    const endpoint = AlcoholApi.delete.endpoint.replace(':alcoholId', String(alcoholId));
    return apiClient.delete<AlcoholDeleteResponse>(endpoint);
  },

  /**
   * 술 수정
   * 전체 수정(PUT)이므로 모든 필드를 전달해야 함
   * 이미 삭제된 술은 수정 불가
   */
  update: async (alcoholId: number, data: AlcoholUpdateRequest): Promise<AlcoholUpdateResponse> => {
    const endpoint = AlcoholApi.update.endpoint.replace(':alcoholId', String(alcoholId));
    return apiClient.put<AlcoholUpdateResponse, AlcoholUpdateRequest>(endpoint, data);
  },
};
